import React, { useState, useEffect, useRef } from 'react';
import CardEditor from '../components/CardEditor';
import TemplateSelector from '../components/TemplateSelector';
import { customCardBorders, customCardIcons, cardEditIcons, cardIconNames } from '../UsefulFunctions';
import { toPng } from 'html-to-image';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBold, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';

// const templates = customCardBorders();
const templates = customCardBorders().map(url => {
  const parts = url.split('/');
  const fileName = parts[parts.length - 1].split('.')[0]; // Get the file name without the extension
  const nameParts = fileName.split('_').slice(0, 2); // Take the first two parts of the name
  var limited = "Basic"
  var rarity = "Common"

  const map1 = new Map();
  map1.set('comm', 'Common')
  map1.set('rare', 'Rare')
  map1.set('epic', 'Epic')
  map1.set('leg', 'Legendary')

  if (nameParts[1].substring(0, 3) === 'Lim') {
    limited = 'Limited'
    rarity = map1.get(nameParts[1].substring(3))
  }
  else {
    rarity = nameParts[1]
  }


  return {
    url: url,
    album: nameParts[0],
    limited: limited,
    rarity: rarity
  };
});

// const icons = customCardIcons()

const iconURLs = customCardIcons();
const iconNames = cardIconNames();

const icons = iconURLs.map((url, index) => ({
  url: url,
  name: iconNames[index]
}));


// Insert <br> at an element's CURRENT soft-wrap boundaries so the export can't
// re-wrap it differently than the editor. The breaks are placed exactly where
// the text already wraps, so there's no visible change; the caller restores the
// original innerHTML afterwards. Handles mixed inline content (text, inline
// icons, <b>); block children (e.g. ability-line rows) reset the run.
const freezeWrap = (el) => {
  if (!el) return;
  const range = document.createRange();
  const atoms = [];
  const isBlock = (node) => {
    const d = window.getComputedStyle(node).display;
    return d === 'block' || d === 'flex' || d === 'grid' || d === 'table' || d === 'list-item';
  };
  const walk = (node) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        for (let i = 0; i < child.data.length; i += 1) atoms.push({ textNode: child, offset: i });
      } else if (child.nodeName === 'IMG') {
        atoms.push({ el: child });
      } else if (child.nodeName === 'BR') {
        atoms.push({ reset: true });
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        if (isBlock(child)) {
          atoms.push({ reset: true });
          walk(child);
          atoms.push({ reset: true });
        } else {
          walk(child);
        }
      }
    });
  };
  walk(el);

  // A text line's height, used as the break threshold. Comparing raw tops is
  // unreliable because an inline icon's box is offset by vertical-align and
  // sits a few px off the text top on the SAME line — so we only treat a jump
  // of at least half a line-height as a real wrap.
  let lineH = 0;
  for (let i = 0; i < atoms.length; i += 1) {
    const a = atoms[i];
    if (a.textNode) {
      range.setStart(a.textNode, a.offset);
      range.setEnd(a.textNode, a.offset + 1);
      const rects = range.getClientRects();
      if (rects.length && rects[0].height) { lineH = rects[0].height; break; }
    }
  }
  if (!lineH) lineH = (parseFloat(window.getComputedStyle(el).fontSize) || 14) * 1.2;
  const threshold = Math.max(3, lineH * 0.5);

  const breaks = [];
  let prevTop = null;
  atoms.forEach((atom, idx) => {
    if (atom.reset) { prevTop = null; return; }
    let top;
    if (atom.el) {
      const r = atom.el.getBoundingClientRect();
      if (!r.height) return;
      top = r.top;
    } else {
      range.setStart(atom.textNode, atom.offset);
      range.setEnd(atom.textNode, atom.offset + 1);
      const rects = range.getClientRects();
      if (!rects.length) return; // collapsed whitespace at a wrap boundary
      top = rects[rects.length - 1].top;
    }
    if (prevTop !== null && top > prevTop + threshold) breaks.push(idx);
    prevTop = top;
  });

  for (let b = breaks.length - 1; b >= 0; b -= 1) {
    const atom = atoms[breaks[b]];
    const br = document.createElement('br');
    if (atom.el && atom.el.parentNode) {
      atom.el.parentNode.insertBefore(br, atom.el);
    } else if (atom.textNode) {
      const after = atom.textNode.splitText(atom.offset);
      after.parentNode.insertBefore(br, after);
    }
  }
};

const saveAsImage = () => {
  const node = document.getElementById('toCapture');
  if (!node) return;

  // Blur any focused card field first — this triggers the focusout whitespace
  // normalization so the saved image collapses leading/extra whitespace too.
  const active = document.activeElement;
  if (active && node.contains(active) && typeof active.blur === 'function') {
    active.blur();
  }

  const doExport = () => {
    // Lock in the editor's exact word-wrap so the saved image can't re-wrap
    // differently (fonts/icons/sub-pixels can otherwise shift a wrap point).
    const freezeTargets = [];
    document
      .querySelectorAll('#abilitiesTable td[contenteditable="true"]')
      .forEach((c) => freezeTargets.push(c));
    const cardName = document.getElementById('card-name');
    if (cardName) freezeTargets.push(cardName);
    const abilityName = document.getElementById('ability-name');
    if (abilityName) freezeTargets.push(abilityName);
    const snapshots = freezeTargets.map((el) => [el, el.innerHTML]);
    freezeTargets.forEach(freezeWrap);
    const restore = () => snapshots.forEach(([el, html]) => { el.innerHTML = html; });

    // Export at the generator's native 1440px width regardless of how large
    // the card is displayed. We DON'T pass an explicit width/height — letting
    // html-to-image render the node at its exact (fractional) on-screen size
    // keeps the layout identical. pixelRatio scales it up to ~1440px wide.
    const rect = node.getBoundingClientRect();
    const pixelRatio = Math.max(1, 1440 / (rect.width || 1));

    // NOTE: do NOT enable cacheBust — it appends a "?t=..." query to every
    // image URL, which corrupts blob:/object-URLs from an uploaded background
    // image (blob URLs can't take query strings) and makes the export reject.
    toPng(node, {
      pixelRatio,
      // Keep the rounded corners / background transparent in the export.
      style: { margin: '0' },
    })
      .then((dataUrl) => {
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = 'cue-custom-card.png';
        link.click();
      })
      .catch((error) => {
        console.error('Error capturing the image:', error);
        alert('Sorry — something went wrong saving the card image. Please try again.');
      })
      .finally(restore);
  };

  // Make sure all webfonts are fully loaded first — otherwise the export can
  // render with fallback metrics and wrap text differently than the editor.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(doExport).catch(doExport);
  } else {
    doExport();
  }
};

function CustomCards() {
  const [selectedTemplate, setSelectedTemplate] = useState(templates[0]);
  const [backgroundImage, setBackgroundImage] = useState('https://t4.ftcdn.net/jpg/04/81/13/43/360_F_481134373_0W4kg2yKeBRHNEklk4F9UXtGHdub3tYk.jpg'); // State to hold the background image
  const [foregroundImage, setForegroundImage] = useState('https://res.cloudinary.com/defal1ruq/image/upload/v1735594880/inverted_add_img_transparent_icon_zepq5o.png'); // State to hold the foreground image
  const isMobile = window.innerWidth <= 600; // Check if the screen width is less than or equal to 600px
  // On desktop use fixed-width icon columns so the palette hugs its content
  // (and sits right next to the card); on mobile keep it proportional.
  const minSize = isMobile ? '8%' : '2.3rem'; // Set the minimum size based on screen size
  const maxSize = isMobile ? '8%' : '2.3rem'; // Set the minimum size based on screen size
  const [isBold, setIsBold] = useState(false);
  // A ref (not a plain variable) so the click handler that records the row and
  // the confirm handler that deletes it share the same value across renders.
  const rowToDeleteRef = useRef(null); // Keep track of the row to delete


  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
  };

  const handleIconSelect = (template) => {
    setForegroundImage(template);
  };

  const handleBackgroundUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file); // Create a URL for the uploaded image
      setBackgroundImage(imageUrl); // Set the background image state
    }
  };

  const handleForegroundUpload = (e) => {
    const file = e.target.files[0];
    // console.log(file)
    if (file) {
      if (file.url && file.url.url) {
        setForegroundImage(file.url.url)
      }
      else if (file.url) {
        setForegroundImage(file.url)
      }
      else {
        const imageUrl = URL.createObjectURL(file); // Create a URL for the uploaded image
        setForegroundImage(imageUrl); // Set the foreground image state
      }
    }
  };

  const handleAbilityDelete = (event) => {
    // Find the table row
    rowToDeleteRef.current = event.target.closest('tr');

    const contentEditableElements = document.querySelectorAll('[contentEditable="true"]');
    contentEditableElements.forEach((el) => {
      el.blur(); // Removes focus
    });

    // Open the confirmation modal
    const modal = document.getElementById('confirmModal');
    if (modal) {
      modal.style.display = 'block';
    }
  };

  const handleCardHelp = (event) => {

    const contentEditableElements = document.querySelectorAll('[contentEditable="true"]');
    contentEditableElements.forEach((el) => {
      el.blur(); // Removes focus
    });

    // Open the confirmation modal
    const modal = document.getElementById('helpModal');
    if (modal) {
      modal.style.display = 'block';
    }
  };

  
  const closeHelpModal = () => {
    const modal = document.getElementById('helpModal');
    if (modal) {
      modal.style.display = 'none'; // Hide the modal
    }
  };

  const confirmDelete = () => {
    const row = rowToDeleteRef.current;
    if (row) {
      const table = row.closest('table');
      row.remove(); // Delete the row
      rowToDeleteRef.current = null; // Clear the reference
      // If that was the last ability, drop the empty table too and re-fit.
      if (table && table.querySelectorAll('tr').length === 0) {
        table.remove();
      }
      const desc = document.getElementById('ability-description');
      if (desc) desc.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Close the modal
    const modal = document.getElementById('confirmModal');
    if (modal) {
      modal.style.display = 'none';
    }
  };

  const cancelDelete = () => {
    // Simply close the modal
    const modal = document.getElementById('confirmModal');
    if (modal) {
      modal.style.display = 'none';
    }
  };

  const closeModal = () => {
    const modal = document.getElementById('confirmModal');
    if (modal) {
      modal.style.display = 'none'; // Hide the modal
    }
  };


  const insertImageAtCursor = (url) => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const parentDiv = document.getElementById('ability-description');

      // Check if the selection is within the ability-description div
      if (parentDiv && parentDiv.contains(range.startContainer)) {

        // Determine image type based on URL or other conditions
        const isAbilityIcon = url.includes('play') || url.includes('return') || url.includes('start');
        let insertedNode;

        if (isAbilityIcon) {
          // Create a new ability line container for play/return/start abilities
          const abilityLine = document.createElement('div');
          abilityLine.className = 'ability-line'; // Style for ability lines

          // Create the image element for the ability icon
          const img = document.createElement('img');
          img.crossOrigin = 'anonymous';
          img.src = url;
          img.alt = ""; // Set alt text for accessibility
          img.className = 'ability-icon'; // Use specific styling for ability icons
          img.style.height = '1.1em'; // ~one line of text tall
          img.style.width = 'auto';

          // Append the icon to the ability line
          abilityLine.appendChild(img);

          // Create a span for the text that should appear to the right of the icon
          const textWrapper = document.createElement('span');
          textWrapper.className = 'text-wrapper';
          textWrapper.textContent = " "; // Optional: add a space for future content
          abilityLine.appendChild(textWrapper);

          // Insert the ability line container at the cursor position
          range.insertNode(abilityLine);
          insertedNode = abilityLine;
        } else {
          // Keep a number (or icon) that is followed by whitespace bound to the
          // icon(s) after it, so "+5 :energy:" (and chains of icons separated by
          // one-or-fewer spaces) never get split across a line break. We do this
          // by turning the single space right before the icon into a
          // non-breaking space.
          const startC = range.startContainer;
          if (startC && startC.nodeType === Node.TEXT_NODE) {
            const t = startC.textContent;
            const off = range.startOffset;
            if (off > 0 && t[off - 1] === ' ') {
              startC.textContent = t.slice(0, off - 1) + '\u00A0' + t.slice(off);
              range.setStart(startC, off);
              range.collapse(true);
            }
          }

          // If it’s a regular inline icon, treat it as text and insert it directly
          const img = document.createElement('img');
          img.crossOrigin = 'anonymous';
          img.src = url;
          img.alt = ""; // Set alt text for accessibility
          img.className = 'inline-icon'; // Styling for inline icons
          img.style.height = '0.9em'; // match the inline text height (top anchored)
          img.style.width = 'auto';
          img.style.verticalAlign = '-0.12em'

          // Insert the inline icon into the current range
          range.insertNode(img);
          insertedNode = img;
        }

        // Clear the selection and move the cursor after the inserted element
        selection.removeAllRanges();
        range.setStartAfter(insertedNode);
        range.collapse(true);
        selection.addRange(range);
      }
    }
  };

  const addAbility = () => {
    const parentDiv = document.getElementById('ability-description');
    const childRef = document.getElementById('editor');

    const childWidth = childRef.offsetWidth;
    const percentage = (childWidth / window.innerWidth) * 100;

    let table = parentDiv.querySelector('table');
    if (!table) {
      table = document.createElement('table');
      table.id = 'abilitiesTable'
      table.style.width = `100%`;
      parentDiv.appendChild(table);
    }

    const modal = document.getElementById('abilityModal');
    modal.style.display = 'block';

    const abilityHandler = (event) => {
      const abilityType = event.target.getAttribute('data-ability');
      let imgUrl;
      let rightFlag = false;

      switch (abilityType) {
        case 'draw':
          imgUrl = 'https://res.cloudinary.com/defal1ruq/image/upload/v1728940001/draw_onlg1u.png';
          rightFlag = true;
          break;
        case 'play':
          imgUrl = 'https://res.cloudinary.com/defal1ruq/image/upload/v1728940003/play_bqxcpx.png';
          rightFlag = true;
          break;
        case 'start':
          imgUrl = 'https://res.cloudinary.com/defal1ruq/image/upload/v1728940005/turnstart_njtwir.png';
          rightFlag = true;
          break;
        case 'return':
          imgUrl = 'https://res.cloudinary.com/defal1ruq/image/upload/v1728940004/return_bd2v6x.png';
          rightFlag = true;
          break;
        default:
          alert("Invalid ability type.");
          return;
      }

      modal.style.display = 'none';

      const row = document.createElement('tr');
      const imgCell = document.createElement('td');
      imgCell.onclick = handleAbilityDelete;
      imgCell.className = 'cellImg';
      imgCell.style.width = `${percentage * 0.1}%`;

      const img = document.createElement('img');
      img.crossOrigin = 'anonymous';
      img.src = imgUrl;
      img.className = 'inline-icon hoverable';
      img.alt = abilityType;
      img.style.height = '1.1em'; // ~one line of text tall
      img.style.width = 'auto';
      if (rightFlag) img.style.float = 'right';

      imgCell.appendChild(img);

      const descCell = document.createElement('td');
      descCell.contentEditable = true;
      descCell.textContent = "Ability description";

      row.appendChild(imgCell);
      row.appendChild(descCell);
      table.appendChild(row);

      // Cleanup: Remove event listener after insertion to avoid duplicates
      document.querySelectorAll('.ability-option').forEach(button => {
        button.removeEventListener('click', abilityHandler);
      });
    };

    document.querySelectorAll('.ability-option').forEach(button => {
      button.addEventListener('click', abilityHandler, { once: true });
    });

    document.querySelector('.closeab').onclick = () => {
      modal.style.display = 'none';
    };

    window.onclick = (event) => {
      if (event.target === modal) {
        modal.style.display = 'none';
      }
    };
  };


  useEffect(() => {
    const root = document.documentElement;

    // Add class to root when component is mounted
    root.classList.add('page-specific');
    // console.log(root.classList)

    // Cleanup function to remove class when component is unmounted
    return () => {
      root.classList.remove('page-specific');
    };
  }, []);

  const toggleBold = (event) => {
    event.preventDefault(); // Prevent losing focus
    setIsBold((prev) => !prev);

    // Toggle bold state
    document.execCommand('bold');
  };

  const adjustButtonHeight = (buttonElement, targetSrc) => {
    if (!buttonElement) return;

    // Find the target image by its src
    const targetImage = document.querySelector(`img[alt="${targetSrc}"]`);
    const parentElement = targetImage.parentElement;
    if (targetImage) {
      // Set the button height to match the target image height
      const targetHeight = parentElement.offsetHeight; // Get height in pixels
      buttonElement.style.height = `${targetHeight}px`;
    };
  }


  return (
    <div className="customCardPage">
      <div style={{ display: "flex", alignItems: "center" }}>
        <h1>Custom Card Editor</h1>
        <FontAwesomeIcon icon={faQuestionCircle} style={{ marginLeft: "10px", fontSize: "1.5rem" }} onClick={handleCardHelp}/>
      </div>
      <TemplateSelector templates={templates} onTemplateSelect={handleTemplateSelect} saveAsImage={saveAsImage} />


      <div className="card-and-controls">
        <CardEditor
          icons={icons}
          onIconSelect={handleIconSelect}
          template={selectedTemplate}
          backgroundImage={backgroundImage}
          foregroundImage={foregroundImage}
          handleForegroundUpload={handleForegroundUpload}
          setForegroundImage={setForegroundImage}
          handleBackgroundUpload={handleBackgroundUpload}
          cardEditIcons={cardEditIcons}
          insertImageAtCursor={insertImageAtCursor}
        />
        {/* Centered Grid Container */}
        <div className='belowCardContainer'>
          <div className='iconGridWrapper'>
            <div className='iconGridContainer'
              style={{
                display: 'grid', // Override display from inline-flex to grid
                gridTemplateColumns: `repeat(7, minmax(${minSize}, ${maxSize}))`, // Updated columns
                gap: '5px', // Retain spacing
                maxWidth: '100%', // Override max-width
                alignItems: 'center',
                alignContent: 'center',
                justifyContent: 'center'
              }}
            >
              {cardEditIcons().map((url, index) => (
                <div key={index} className='iconImageWrapper'>
                  <img src={url} alt={`${index + 1}`} className='iconImage' onClick={() => insertImageAtCursor(url)} style={{ cursor: 'pointer' }} />
                </div>
              ))}
              <div className='iconImageWrapper' ref={(el) => adjustButtonHeight(el, '1')}>
                <button className={`boldButton ${isBold ? 'active' : ''}`} onMouseDown={(e) => toggleBold(e)}>
                  <FontAwesomeIcon icon={faBold} className={isBold ? 'boldActive' : ''} />
                </button>
              </div>
            </div>
          </div>
          <div>
            <button id="add-ability-button" onClick={addAbility}>Add ability</button>
          </div>
        </div>
      </div>
      <div id="abilityModal" className="modal">
        <div className="modal-content">
          <span className="closeab">&times;</span>
          <h2>Select an Ability</h2>
          <button className="ability-option" data-ability="draw" style={{ backgroundColor: '#90ff8d' }}>Draw</button>
          <button className="ability-option" data-ability="play" style={{ backgroundColor: '#ff9149' }}>Play</button>
          <button className="ability-option" data-ability="start" style={{ backgroundColor: '#f2de7f' }}>Start</button>
          <button className="ability-option" data-ability="return" style={{ backgroundColor: '#d4b4ff' }}>Return</button>
        </div>
      </div>
      <div id="confirmModal" className="modal">
        <div className="modal-content">
          <span className="closeabconfirm" onClick={closeModal}>&times;</span>
          <h2>Are you sure you want to remove this ability?</h2>
          <div>
            <button className="ability-option" style={{ backgroundColor: '#ff4d4d' }} onClick={confirmDelete}>Yes, Delete</button>
            <button className="ability-option" style={{ backgroundColor: '#4caf50' }} onClick={cancelDelete}>Cancel</button>
          </div>
        </div>
      </div>
      <div id="helpModal" className="modal">
        <div className="modal-content" style={{ color: "black" }}>
          <span className="closeHelp" onClick={closeHelpModal}>&times;</span>
          <h2>Images:</h2>
          <h4>Click on the background to add a card image! Click where the collection icon should go to search for and add an existing collection icon.</h4>
          <h2>Name, Code, and Energy/Power:</h2>
          <h4>Simply click to edit any text on the card!</h4>
          <h2>Abilities:</h2>
          <h4>Use the "Add Ability" button to add abilities to your card! This lets you pick an ability type. You can click on the provided icons 
            (ex. power/burn/etc.) to add them. The "B" button will toggle bold on and off.
          </h4>
          <h2>Save Your Card!</h2>
          <h4>You can use the "Save Image" button to save your card, or just take a screenshot yourself!</h4>
        </div>
      </div>



    </div>
  );

}

export default CustomCards;
